import React, { useRef, useMemo } from "react";
import useCustomTranslation from "../../utils/useCustomTranslation";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./CardCarouselSlider.css";
import ImageWithObjectFit from "../ImageWrapperComponent";
import { useSelector } from "react-redux";
import * as posterImage from "../../utils/usePosterImage";
import useGetArticleMeta from "../../utils/useGetArticleMeta";
import { APP_COLORS } from "../../constants/styles";
import { Link } from "react-router-dom";
import { getTargetFromType, useGetLink } from "../../utils/useOpenLink";
import CustomLink from "../../utils/customLink";
import { Button } from "../Button";
import { ARTICLE_DETAILS } from "../../constants/strings";
import PlayIconSvg from "../../assets/PlayIconSvg";

function CardCarouselSlider({
    contents: contentToRender = [],
    heading,
    colors: colorObj,
    block_buttons,
    block_source,
}) {
    const { direction } = useCustomTranslation();

    const prevRef = useRef(null);

    const nextRef = useRef(null);

    const { getTheme } = useGetArticleMeta();
    const { getLink } = useGetLink();

    const content = useSelector((state) => state.layout.content);
    // Remove setCurrentContent and useEffect, use useMemo instead
    const contentReferenceData = useMemo(() => {
        if (block_source === ARTICLE_DETAILS) {
            if (contentToRender?.[0]?.contentKey) {
                const contentKey = contentToRender[0].contentKey;
                const respKeys = content[contentKey];
                let resp = respKeys?.map((k) => content[k]) || [];
                resp = JSON.parse(JSON.stringify(resp));

                for (const element of resp) {
                    if (element?.link) {
                        element.posterImage = posterImage.usePosterImage(
                            element.link,
                            element.image,
                        );
                    }
                }

                return resp;
            }
        } else {
            const arr = contentToRender
                .filter((c) => c.content_source === "Content")
                .map((c) => ({
                    image: c.content_image,
                    title: c.heading,
                    path: c.content_reference,
                    type: c.type,
                    content_source: c.content_source,
                }));

            return arr;
        }

        return [];
    }, [block_source, contentToRender, content]);

    return (
        <section
            className="cardCarouselSlideWrapper py-5 bg-background_color"
            data-aos="fade-up"
            style={APP_COLORS(colorObj)}
        >
            <div className="container mx-auto px-4">
                <div className="flex">
                    <h2 className="mainHeading text-accent_color1 mb-4">{heading}</h2>
                    {block_buttons?.length > 0 && (
                        <div className="justify-end ml-auto">
                            {block_buttons.map((v) => (
                                <CustomLink to={getLink(v.action)} key={v.label}>
                                    <Button
                                        key={v.label}
                                        className="morebtn"
                                        style={{ "--accent_color3": colorObj?.accent_color3 }}
                                    >
                                        {v.label}
                                        <i
                                            className="pi pi-angle-right mt-1 ml-2"
                                            aria-hidden="true"
                                        ></i>
                                    </Button>
                                </CustomLink>
                            ))}
                        </div>
                    )}
                </div>

                <div className="relative">
                    <button
                        ref={prevRef}
                        className="swiper-button-prev-custom text-primary_color bg-accent_color4"
                    ></button>
                    <button
                        ref={nextRef}
                        className="swiper-button-next-custom text-primary_color bg-accent_color4"
                    ></button>

                    <div className="col-12">
                        <Swiper
                            dir={direction}
                            key={`swiper-direction-${direction}`}
                            spaceBetween={10}
                            slidesPerView={4.5}
                            modules={[Pagination, Navigation]}
                            pagination={{
                                clickable: true,
                                dynamicBullets: true,
                                dynamicMainBullets: 7,
                            }}
                            navigation={{
                                prevEl: prevRef.current,
                                nextEl: nextRef.current,
                            }}
                            onBeforeInit={(swiper) => {
                                swiper.params.navigation.prevEl = prevRef.current;
                                swiper.params.navigation.nextEl = nextRef.current;
                            }}
                            breakpoints={{
                                0: {
                                    slidesPerView: 2.5,
                                    spaceBetween: 10,
                                },
                                768: {
                                    slidesPerView: 3,
                                    spaceBetween: 15,
                                },
                                1024: {
                                    slidesPerView: 4.5,
                                    spaceBetween: 20,
                                },
                            }}
                        >
                            {contentReferenceData.map((v, idx) => (
                                <SwiperSlide key={idx}>
                                    <div className="overflow-hidden">
                                        <div className="cardImg">
                                            {!!v?.link && (
                                                <div
                                                    className="playIconHldr"
                                                    style={{
                                                        "--accent_color4": colorObj.accent_color4,
                                                    }}
                                                >
                                                    <PlayIconSvg />
                                                </div>
                                            )}
                                            <Link
                                                to={getLink(v?.path, v?.type, v?.language, v?.link)}
                                                target={getTargetFromType(v?.type, v?.link)}
                                            >
                                                <ImageWithObjectFit src={v.image?.src} />
                                            </Link>
                                        </div>
                                        <div className="pt-4">
                                            <div className="themeText text-primary_color">
                                                {getTheme(v?.breadcrumb)}
                                            </div>
                                            <Link
                                                to={getLink(v?.path, v?.type, v?.language, v?.link)}
                                                target={getTargetFromType(v?.type, v?.link)}
                                            >
                                                <h3 className="titleText hover:underline text-accent_color1">
                                                    {v.title}
                                                </h3>
                                            </Link>
                                        </div>
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default CardCarouselSlider;
